import { isApproverRole, type MeResponse, type Role } from '@time-tracking/shared';

/**
 * Display label for each role, in the app's own words.
 *
 * Lives here rather than in any one feature folder because it is shared chrome:
 * the header's account menu (Layout), the Help & FAQ section note, and the
 * defect-report prefill all render the same label for the same role. Adding a
 * role to `Role` fails to compile until this map covers it.
 */
export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  contributor: 'Contributor',
  approver: 'Approver',
  administrator: 'Administrator',
};

function isRole(value: string | undefined): value is Role {
  return value !== undefined && Object.hasOwn(ROLE_LABELS, value);
}

/**
 * Display label for a role string, defaulting to "Contributor".
 *
 * Deliberately tolerant of `string | undefined`: `useMe` returns `undefined`
 * data on first paint (`enabled: Boolean(idToken)`), and the header renders a
 * role label on every page. Narrowing the parameter to `Role` would regress the
 * header on first paint everywhere.
 */
export function getRoleLabel(role: string | undefined): string {
  return isRole(role) ? ROLE_LABELS[role] : ROLE_LABELS.contributor;
}

/**
 * Whether a user may access the Projects & Tasks area.
 *
 * Approvers and administrators always can — via `isApproverRole`, the shared
 * definition of that two-member set, rather than a fresh inline copy of it. A
 * contributor can too, but only if they lead at least one project — "lead" is
 * not a role (I-02: three roles only), so a non-approver lead's `role` is still
 * `contributor` (slice S-A).
 *
 * This is the single source of truth for both the nav link (Layout) and the
 * route guard (Router). Keeping them in sync here prevents the failure where
 * the nav shows "Projects & tasks" but the route guard bounces the user back
 * to Home.
 */
export function canAccessProjects(me: MeResponse | undefined): boolean {
  if (!me) return false;
  return isApproverRole(me.role) || (me.leadsAnyProject ?? false);
}
